import { useConst } from '@execonline-inc/react-hooks.private';
import { just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import { findLinkBy } from '../../../../../../../Links';
import NotificationSource from '../../../../../../../NotificationSource';
import { useLearningCollection } from '../../LearningCollectionContext';
import { useEditSectionLoader } from '../../Edit/SectionsLoader/useEditSectionLoader';
import { useNewSectionsLoader } from '../../New/SectionsLoader/useNewSectionsLoader';
import BaseLayout from '../../../../shared/SaveActions/BaseLayout';
import CancelButton from '../../../../shared/SaveActions/CancelButton';
import PublishButton from '../../../../shared/SaveActions/PublishButton';
import CancelModal from './CancelModal';
import ConfirmationModal from './ConfirmationModal';
import CreateButton from './CreateButton';
import SaveButton from './SaveButton';
import SaveOptionsDropdown from './SaveOptionsDropdown';
import SaveActionsStore from './Store';

interface Props {
  stateKind: 'edit' | 'new';
}

function BuilderV2SaveActions({ stateKind }: Props) {
  const store = useConst(() => new SaveActionsStore());
  const { resourceState } = useLearningCollection();
  const { sendSaveNewRequest } = useNewSectionsLoader(store);
  const { sendSaveEditRequest } = useEditSectionLoader(store);

  // Validation: Check if learning collection and product collection titles are valid
  const hasValidationErrors = () => {
    // Check learning collection title
    const learningCollectionName = resourceState.payload.name?.trim() || '';
    if (!learningCollectionName || learningCollectionName.length > 255) {
      return true;
    }

    // Check all product collection titles
    const hasInvalidProductCollection = resourceState.payload.product_collections.some((pc) => {
      const productCollectionName = pc.payload.name?.trim() || '';
      return !productCollectionName || productCollectionName.length > 255;
    });

    return hasInvalidProductCollection;
  };

  const isDisabled =
    store.state.kind === 'saving' || store.state.kind === 'publishing' || hasValidationErrors();

  const handleCancel = () => {
    store.showCancelConfirmation();
  };

  const handlePublish = () => {
    store.showPublishConfirmation();
  };

  const handleCreateWithoutPublishing = () => {
    store.showCreateConfirmation();
  };

  const handleConfirmPublish = () => {
    store.confirmPublish();
    const updatedResource = {
      ...resourceState,
      payload: {
        ...resourceState.payload,
        published_at: just<Date>(new Date()),
      },
    };
    sendSaveNewRequest(updatedResource, true);
  };

  const handleConfirmCreate = () => {
    store.confirmCreate();
    const updatedResource = {
      ...resourceState,
      payload: {
        ...resourceState.payload,
        published_at: nothing<Date>(),
      },
    };
    sendSaveNewRequest(updatedResource, false);
  };

  const handleCancelConfirmation = () => {
    store.cancelConfirmation();
  };

  const handleSave = () => {
    sendSaveEditRequest(resourceState);
  };

  if (stateKind === 'edit') {
    return (
      <>
        <NotificationSource store={store} />
        <BaseLayout>
          <CancelButton onClick={handleCancel} disabled={isDisabled} />
          <SaveButton onClick={handleSave} disabled={isDisabled} />
        </BaseLayout>
        {store.state.kind === 'confirming-cancel' && (
          <CancelModal isOpen={true} onClose={handleCancelConfirmation} />
        )}
      </>
    );
  }

  // Render action buttons based on publish permission
  // If publish link exists: show Publish button with dropdown
  // If no publish link: show only Create button
  const actionButtons = findLinkBy({ rel: 'publish' }, resourceState.links)
    .map(() => (
      <div className="inline-flex rounded-md shadow-sm">
        <PublishButton onClick={handlePublish} disabled={isDisabled} />
        <SaveOptionsDropdown
          onCreateWithoutPublishing={handleCreateWithoutPublishing}
          disabled={isDisabled}
        />
      </div>
    ))
    .getOrElse(() => (
      <CreateButton onClick={handleCreateWithoutPublishing} disabled={isDisabled} />
    ));

  // Render publish confirmation modal only if user has publish permission
  const publishConfirmationModal = findLinkBy({ rel: 'publish' }, resourceState.links)
    .map(() =>
      store.state.kind === 'confirming-publish' ? (
        <ConfirmationModal
          isOpen={true}
          action="Publish"
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmPublish}
        />
      ) : null,
    )
    .getOrElse(() => null);

  return (
    <>
      <NotificationSource store={store} />
      <BaseLayout>
        <CancelButton onClick={handleCancel} disabled={isDisabled} />
        {actionButtons}
      </BaseLayout>
      {store.state.kind === 'confirming-cancel' && (
        <CancelModal isOpen={true} onClose={handleCancelConfirmation} />
      )}
      {publishConfirmationModal}
      {store.state.kind === 'confirming-create' && (
        <ConfirmationModal
          isOpen={true}
          action="Create"
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmCreate}
        />
      )}
    </>
  );
}

export default observer(BuilderV2SaveActions);
